import {
  Center,
  Heading,
  VStack,
  Text,
  Stack,
  Button,
  HStack,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Icon } from '$/components/common/Icon';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { HookFormInput } from '$/components/core/Form/HookFormInput';
import { useCloudMessageTranslation } from '$/hooks/useCloudMessageTranslation';
import { useToast } from '$/hooks/useToast';
import { type StudentInvitationRequest } from '$/services/usecases/invitation/mapper/invitation';
import { acceptStudentInvitation } from '$/services/usecases/school/students';
import { isPasswordValid } from '$/utils/regexUtils';

interface Props {
  invitation: StudentInvitationRequest;
}

type FormData = {
  password: string;
};

export const StudentInvitationPage: FC<Props> = ({ invitation }) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const { cloudMessageTranslation } = useCloudMessageTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const setPendingAuthToken = useAuthenticationStore.useSetPendingAuthToken();
  const setPendingAuthEmail = useAuthenticationStore.useSetPendingAuthEmail();
  const onModalOpen = useAuthenticationStore.useOnModalOpen();
  const formMethods = useForm<FormData>({
    mode: 'onTouched',
    defaultValues: {
      password: '',
    },
  });
  const { mutate, isPending } = useMutation({
    mutationFn: acceptStudentInvitation,
    onSuccess: (data) => {
      if (data.isSuccessful) {
        if (data.response.payload?.otpToken) {
          setPendingAuthToken(data.response.payload.otpToken);
          setPendingAuthEmail(invitation.email);
          onModalOpen('registerOtp');
        }
        return void navigate({ to: '/' });
      } else {
        toast(cloudMessageTranslation(data.response.message), 'error', '');
      }
    },
    onError: (error) => {
      console.error('Error accepting invitation:', error);
    },
  });

  const onAccept = () => {
    mutate({
      id: invitation.id,
      password: formMethods.getValues('password'),
    });
  };

  return (
    <Center h='100dvh'>
      <VStack layerStyle='DashboardSection' gap='4' maxW='31rem' p='2.5rem'>
        <Heading fontSize='3xl' textAlign='center'>
          {t('school.studentInvitation.title')}
        </Heading>
        <Text fontSize='sm'>
          {t('school.studentInvitation.description', {
            schoolClassName: invitation.schoolClassName,
            schoolName: invitation.schoolName,
          })}
        </Text>
        {invitation.isNewAccount && (
          <FormProvider {...formMethods}>
            <Stack as='form' onSubmit={formMethods.handleSubmit(onAccept)}>
              <Text fontSize='sm'>
                {t('school.studentInvitation.passwordDescription')}
              </Text>
              <HookFormInput
                type={showPassword ? 'text' : 'password'}
                accessor='password'
                label={t('dashboard.authentication.insertPasswordLabel')}
                placeholder={t('dashboard.authentication.defaultPlaceholder', {
                  field: t('dashboard.authentication.passwordLabel'),
                })}
                registerOptions={{
                  required: t(
                    'dashboard.authentication.errorMessages.isRequired',
                  ),
                  validate: {
                    passwordValid: (v) =>
                      isPasswordValid(String(v)) ||
                      t(
                        'dashboard.authentication.errorMessages.passwordRulesMismatch',
                      ),
                  },
                }}
                inputRightElement={
                  <Button
                    onClick={() => setShowPassword((state) => !state)}
                    variant='text'
                  >
                    <Icon
                      icon={showPassword ? 'visibility_on' : 'visibility_off'}
                    />
                  </Button>
                }
                description={t(
                  'dashboard.authentication.errorMessages.passwordRules',
                )}
              />
            </Stack>
          </FormProvider>
        )}
        {!invitation.isNewAccount && (
          <Text fontSize='sm'>
            {t('school.studentInvitation.accountTranformationInfo')}
          </Text>
        )}
        <HStack mt='2'>
          <Button
            isDisabled={
              invitation.isNewAccount && !formMethods.formState.isValid
            }
            isLoading={isPending}
            onClick={onAccept}
          >
            {t('school.studentInvitation.acceptButton')}
          </Button>
        </HStack>
      </VStack>
    </Center>
  );
};
